import { useEffect, useState } from "react";
import { CustomDataGrid } from "../../components/common/CustomDataGrid"
import axios from "axios";
import baseURL from "../../configs/api";
import { GridColDef } from "@mui/x-data-grid";
import { useTheme } from "../../components/hooks/useTheme";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import useTenant from "../../components/hooks/useTenant";
import useGeneral from "../../components/hooks/useGeneral";
import moment from "moment-timezone";
import { repairOrderStatusColorCode } from "../../utils/statusColorCode";

type Props = {
  date: string;
  branchId: number;
  branchTimeZone: string;
};

export const RepairsIn = ({ date, branchId, branchTimeZone }: Props) => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { themeStyles } = useTheme();
  const { handleTenantError }: any = useTenant();
  const { timeZoneName }: any = useGeneral();
  const [data, setData] = useState<any[]>([]);
  const [filterData, setFilterData] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const columns: GridColDef[] = [
    {
      field: "repairOrderId",
      headerName: "Id",
      headerClassName: themeStyles.table.headerBg,
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return (
          <Link to="/fleet/repairorders/editrepairorder" state={params?.row?.repairOrderId}
            style={{
              color: "var(--app-link-color)",
              textDecoration: "underline",
            }}
          >
            RP-{params?.value}
          </Link>
        )
      }
    },
    {
      field: "makeName",
      headerClassName: themeStyles.table.headerBg,
      headerName: "Vehicle Make",
      editable: false,
      sortable: false,
      flex: 1
    },
    {
      field: "modelName",
      headerName: "Vehicle Model",
      headerClassName: themeStyles.table.headerBg,
      editable: false,
      sortable: false,
      flex: 1
    },
    {
      field: "vehicleClassName",
      headerName: "Vehicle Class",
      headerClassName: themeStyles.table.headerBg,
      editable: false,
      sortable: false,
      flex: 1,
    },
    {
      field: "vehicle",
      headerName: "Vehicle",
      headerClassName: themeStyles.table.headerBg,
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return (
          <Link to="/fleet/vehicle/editvehicles" state={params?.row?.vehicleId}
            style={{
              color: "var(--app-link-color)",
              textDecoration: "underline",
            }}
          >
            {params?.value}
          </Link>
        )
      }
    },
    {
      field: "repairOrderStatus",
      headerName: "Status",
      headerClassName: themeStyles.table.headerBg,
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        let color = {
          colorBg: "#000000",
          colorText: "#000000"
        };
        color = repairOrderStatusColorCode?.find((color: any) => color?.label === params?.value?.trim()?.toLowerCase()) || {
          colorBg: "#000000",
          colorText: "#000000"
        };

        return (
          <div className="flex items-end h-full">
            <span className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold ${color.colorBg} ${color.colorText}`}>
              {(Number(params.value) === 0 ? "None" : params.value) || "None"}
            </span>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    if (date) {
      setLoading(true);

      if (branchId > 0) {
        const updatedDate = moment.tz(date, branchTimeZone).endOf('day');
        let formatDateString = updatedDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

        axios
          .get(`${baseURL}dailyManifest/RepairsInByBranchId/${formatDateString},${branchId}`, {
            headers: {
              'X-Tenant-ID': tenantId,
              "Content-Type": "application/json"
            },
          })
          .then((response) => {
            const result = response?.data?.map((item: any, index: number) => {
              return {
                ...item,
                id: index,

              }
            })
            setData(result);
            setFilterData(result);
            setLoading(false);
            handleTenantError(null);
          })
          .catch((error) => {
            handleTenantError(error);
          });
      } else {
        const updatedDate = moment.tz(date, timeZoneName).endOf('day');
        let formatDateString = updatedDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

        axios
          .get(`${baseURL}dailyManifest/RepairsIn/${formatDateString}`, {
            headers: {
              'X-Tenant-ID': tenantId,
              "Content-Type": "application/json"
            },
          })
          .then((response) => {
            const result = response?.data?.map((item: any, index: number) => {
              return {
                ...item,
                id: index,

              }
            })
            setData(result);
            setFilterData(result);
            setLoading(false);
            handleTenantError(null);
          })
          .catch((error) => {
            handleTenantError(error);
          });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, timeZoneName, branchId, branchTimeZone]);

  useEffect(() => {
    setFilterData(data?.filter((_: any) =>
      _?.makeName?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
      _?.modelName?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
      _?.vehicleClassName?.toLowerCase()?.includes(searchValue?.toLowerCase())
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  return (
    <div className={`w-full border ${themeStyles.main.borderColor} rounded-[16px]`}>
      <div className={`flex justify-between items-center p-4 border-b ${themeStyles.main.borderColor}`}>

        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Repairs In</h2>

        <div className={`w-[30%] flex px-[5px] py-[6px] items-center gap-2.5 rounded-lg border ${themeStyles.main.borderColor} shadow-sm`}>
          <span className={`material-symbols-outlined text-[25px] font-light ${themeStyles.main.textSubHeadingColor}`}>
            search
          </span>
          <input
            type="text"
            placeholder="Vehicle Make, Model or Class"
            className={`border-none outline-none w-full ${themeStyles.main.textSubHeadingColor} bg-transparent text-sm`}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
      </div>

      <CustomDataGrid
        filteredRows={filterData}
        columns={columns}
        loading={loading}
      />
    </div>
  )
}